// DataTrace TaxSource API Service
// Provides tax search and verification for properties
import { getCredentials } from './datatraceApi';

// Get tax data for a property via proxy server
export const searchTaxByProperty = async (searchParams) => {
  console.log('Searching DataTrace TaxSource for:', searchParams);

  // Get DataTrace credentials (same as property search)
  const credentials = getCredentials();

  let response;
  try {
    response = await fetch('/api/datatrace/tax-search', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        credentials: {
          clientId: credentials.clientId,
          clientSecret: credentials.clientSecret,
          scope: credentials.scope || `${credentials.clientId}/.default`
        },
        searchParams
      }),
    });
  } catch (fetchError) {
    const errorDetails = [
      `Network Error: ${fetchError.message}`,
      '',
      'Make sure the proxy server is running (npm run server)',
      '',
      '--- Search Parameters ---',
      JSON.stringify(searchParams, null, 2)
    ].join('\n');
    throw new Error(errorDetails);
  }

  const responseText = await response.text();
  console.log('Tax Search Proxy Response:', responseText);

  let data;
  try {
    data = JSON.parse(responseText);
  } catch (parseError) {
    const errorDetails = [
      `JSON Parse Error: ${parseError.message}`,
      '',
      '--- Raw Response ---',
      responseText
    ].join('\n');
    throw new Error(errorDetails);
  }

  // Check for error response from proxy
  if (!response.ok || data.error) {
    const errorDetails = [
      `Error: ${data.error || `HTTP ${response.status}`}`,
      '',
      data.httpStatus ? `--- HTTP Status ---\n${data.httpStatus} ${data.httpStatusText || ''}` : '',
      '',
      '--- Details ---',
      data.details || '(no details)',
      '',
      data.rawResponse ? '--- Raw Response ---\n' + data.rawResponse : '',
      '',
      data.parseError ? '--- Parse Error ---\n' + data.parseError : '',
      '',
      data.errors ? '--- API Errors ---\n' + JSON.stringify(data.errors, null, 2) : '',
      '',
      data.fullResponse ? '--- Full Response ---\n' + JSON.stringify(data.fullResponse, null, 2) : '',
      '',
      '--- Request Sent ---',
      JSON.stringify(data.request || searchParams, null, 2)
    ].filter(Boolean).join('\n');
    throw new Error(errorDetails);
  }

  return transformTaxResponse(data.data, searchParams);
};

// Transform DataTrace Tax API response to our app format
const transformTaxResponse = (apiResponse, searchParams) => {
  const data = apiResponse.data || {};
  const taxInfo = data.tax_information || data.tax_info || {};
  const property = data.subject_property || data.property || {};
  const situsAddress = property.situs_address || {};

  // Extract tax details
  const currentYearTax = taxInfo.current_year || taxInfo.tax_year || {};
  const taxBills = taxInfo.tax_bills || [];
  const assessments = taxInfo.assessments || [];

  return {
    // Raw API response for reference
    rawResponse: apiResponse,

    property: {
      address: situsAddress.street_address || searchParams.address || 'N/A',
      city: situsAddress.city || searchParams.city || 'N/A',
      state: situsAddress.state || searchParams.state || 'N/A',
      zip: situsAddress.zip || searchParams.zip || 'N/A',
      county: situsAddress.county || searchParams.county || 'N/A',
      apn: situsAddress.parcel_id || searchParams.apn || 'N/A'
    },

    tax: {
      taxYear: currentYearTax.tax_year || new Date().getFullYear().toString(),
      totalTax: currentYearTax.total_tax || currentYearTax.annual_tax || 'N/A',
      status: currentYearTax.status || 'Unknown',
      paid: currentYearTax.paid === true || currentYearTax.status?.toLowerCase().includes('paid'),

      // Payment details
      firstInstallment: {
        amount: currentYearTax.first_installment_amount || 'N/A',
        dueDate: currentYearTax.first_installment_due || 'N/A',
        paid: currentYearTax.first_installment_paid || false
      },
      secondInstallment: {
        amount: currentYearTax.second_installment_amount || 'N/A',
        dueDate: currentYearTax.second_installment_due || 'N/A',
        paid: currentYearTax.second_installment_paid || false
      },

      // Assessment values
      taxRateArea: currentYearTax.tax_rate_area || 'N/A',
      assessedLand: currentYearTax.assessed_land_value || 'N/A',
      assessedImprovement: currentYearTax.assessed_improvement_value || 'N/A',
      totalAssessed: currentYearTax.total_assessed_value || 'N/A',
      marketValue: currentYearTax.market_value || 'N/A',

      // Exemptions
      exemptions: (currentYearTax.exemptions || []).map(ex => ({
        type: ex.exemption_type || ex.type || 'Unknown',
        amount: ex.amount || 'N/A',
        description: ex.description || ''
      })),

      // Historical tax bills
      taxBills: taxBills.map(bill => ({
        year: bill.tax_year || 'N/A',
        amount: bill.total_tax || 'N/A',
        status: bill.status || 'N/A',
        paid: bill.paid === true
      })),

      // Assessment history
      assessments: assessments.map(assessment => ({
        year: assessment.year || 'N/A',
        landValue: assessment.land_value || 'N/A',
        improvementValue: assessment.improvement_value || 'N/A',
        totalValue: assessment.total_value || 'N/A'
      }))
    },

    // DataTrace specific metadata
    metadata: {
      transactionId: apiResponse.transaction_id,
      confirmationNumber: apiResponse.confirmation_number,
      productName: apiResponse.product_name,
      reportStatus: apiResponse.report_status,
      orderItemId: apiResponse.order_item_id
    }
  };
};

/**
 * Check the status of a tax search transaction
 * Uses the generic checkTransactionStatus from datatraceTransactions
 * @param {string} transactionId - Transaction ID from initial search
 * @returns {Promise<Object>} - Status response
 */
export const checkTaxStatus = async (transactionId) => {
  const { checkTransactionStatus } = await import('./datatraceTransactions');
  return checkTransactionStatus(transactionId);
};

/**
 * Get the completed tax report
 * Uses the generic getTransactionReport from datatraceTransactions
 * @param {string} transactionId - Transaction ID
 * @param {string} productCode - Product code (default: 'tax')
 * @returns {Promise<Object>} - Report data
 */
export const getReport = async (transactionId, productCode = 'tax') => {
  const { getTransactionReport } = await import('./datatraceTransactions');
  return getTransactionReport(transactionId, productCode);
};

export default {
  searchTaxByProperty,
  checkTaxStatus,
  getReport
};
